import React, { useState, useCallback, useContext, useEffect } from 'react';
import clsx from 'clsx';

import { useDropzone } from 'react-dropzone';

import {
    Button,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Tooltip,
    CircularProgress,
    colors,
    Paper,
} from '@material-ui/core';

import PublishIcon from '@material-ui/icons/Publish';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import { makeStyles } from '@material-ui/styles';
const useStyles = makeStyles((theme) => ({
    root: {

    },
    dropZone: {
        border: "1px",
        padding: "8px",
        outline: "none",
        display: "flex",
        flexWrap: "wrap",
        backgroundColor: "#e1e1e1",
        opacity: 1,
        "&:hover": {
            backgroundColor: "#f1f1f1",
            opacity: 1,
            cursor: "pointer"
        }
    },
    dragActive: {
        backgroundColor: colors.grey[50],
        opacity: 0.5,
    },
    disabled: {
        border: "1px",
        padding: "8px",
        outline: "none",
        display: "flex",
        flexWrap: "wrap",
        backgroundColor: "#e1e1e1",
        opacity: 1,
        borderColor: "#B8B8B8",
        color: "#B8B8B8"
    },
    item: {
    },
    image: {
        width: 130
    },
    info: {
        marginTop: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: '100%'
    },
    list: {
    },
    thumbnail: {
        width: "48px",
        height: "48px",
        marginRight: theme.spacing(1)
    },
    actions: {
        marginTop: 2,
        display: "flex",
        justifyContent: "flex-end",
        "& > * + *": {
            marginLeft: 2
        }
    },
    removeButton: {
        marginRight: 10
    }
}));

export const UploadFile = ({
    height,
    title,
    singleFile,
    dataField,
    disabled,
    upload,
    onFileDrop,
    onFileUploaded,
    helpText,
    uploadOnDrop,
    uploadButtonText,
    removeButtonText,
    removeAllButtonText,
    ...props }) => {
    const classes = useStyles();

    const [files, setFiles] = useState([]);

    const handleDrop = useCallback((acceptedFiles) => {
        if (onFileDrop && !onFileDrop(acceptedFiles)) {
            return;
        }
        setFiles((prevFiles) => {
            if (singleFile) return [].concat(acceptedFiles[0]);
            else return [...prevFiles].concat(acceptedFiles);
        });
    }, []);

    const [isUploading, setIsUploading] = useState(false);
    const uploadAllFilesClickHandler = async () => {

        setIsUploading(true);
        for (const file of files) {
            try {
                const data = new FormData();
                data.append(dataField ?? "file", file);
                if (upload) {
                    const success = await upload(data, file);
                    if (!success) throw "Unable to upload file";
                }
                setFiles((prevFiles) => [...prevFiles].filter(x => x.name !== file.name));
                if (onFileUploaded) onFileUploaded(file, true);

            } catch (e) {
                if (onFileUploaded) onFileUploaded(file, false);
                console.debug(e);
            }
        }

        setIsUploading(false);
    }

    const removeFileClickHandler = (index) => {
        const newFiles = [].concat(files)
        newFiles.splice(index, 1)
        setFiles(newFiles)
    }

    const removeAllFilesClickHandler = () => {
        setFiles([])
    }

    const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({ onDrop: handleDrop, disabled });

    useEffect(() => {
        if (uploadOnDrop && files && files.length > 0) {
            uploadAllFilesClickHandler();
        }
    }, [files])

    return (
        <div style={{ zIndex: 100, maxWidth: "100%" }}>
            <div
                className={clsx({
                    [classes.dropZone]: true && !disabled,
                    [classes.dragActive]: isDragActive && !disabled,
                    [classes.disabled]: disabled
                })}
                {...getRootProps()}
                style={{ borderStyle: 'dashed', borderWidth: 1, borderRadius: 10, height: height ? height : 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <input {...getInputProps()} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', alignContent: 'center', justifyContent: 'center', width: '100%' }}>
                    <Typography
                        className={classes.info}
                        gutterBottom
                        variant='h6'
                        align='center'
                        noWrap>
                        {title}
                    </Typography>
                    <CloudUploadIcon />
                    <Typography
                        className={classes.info}
                        variant='body1'
                        align='center'
                        noWrap>
                        {helpText ?? <>Drop files here or click <Link color="inherit" underline="always">browse</Link> thorough your machine</>}
                    </Typography>
                </div>

            </div>
            {
                !uploadOnDrop && files && files.length > 0 && (
                    <>
                        <List className={classes.list}>
                            {files.map((file, i) => (
                                <ListItem
                                    disableGutters
                                    className={classes.item}
                                    divider={i < files.length - 1}
                                    key={i}>
                                    <ListItemIcon>
                                        {!isUploading ?
                                            <PublishIcon />
                                            :
                                            <CircularProgress size="1.6em" color="primary" />
                                        }
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.name}
                                        primaryTypographyProps={{ variant: 'body1' }}
                                    />
                                    {!isUploading && !singleFile &&
                                        <Tooltip title={removeButtonText ?? "Remove"}>
                                            <IconButton
                                                color="primary"
                                                edge="end"
                                                onClick={() => removeFileClickHandler(i)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                </ListItem>
                            ))}
                        </List>
                        <div className={classes.actions}>
                            {!singleFile &&
                                <Button
                                    color="primary"
                                    className={classes.removeButton}
                                    size="small"
                                    disabled={disabled && isUploading}
                                    onClick={removeAllFilesClickHandler}>
                                    {removeAllButtonText ?? "Remove All"}
                                </Button>
                            }
                            <Button
                                color="primary"
                                size="small"
                                variant="contained"
                                disabled={disabled && isUploading}
                                onClick={uploadAllFilesClickHandler}>
                                {uploadButtonText ?? "Upload"}
                            </Button>
                        </div>
                    </>
                )
            }
        </div >
    );
}
